import React, {
  useState,
  useRef,
} from 'react';
import isNil from 'lodash/isNil';
import IconButton from '@mui/material/IconButton';
import ToolTip from 'core/components/Tooltip';
import {
  TemplateIcon,
} from 'core/icons';
import {
  ToolbarItemProps,
} from 'views/EMR/components/TextAreaToolbar';
import TemplateDialog, {
  VisitNoteTemplateDialogAction,
  VisitNoteTemplateDialogParams,
} from 'views/EMR/components/VisitNoteTemplateDialog';
import {
  normalizeContent,
  isEmptyRichText,
  EMPTY_CKE_TEXT,
} from 'views/EMR/Settings/helper';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  MultiRootEditor,
  type ModelWriter,
} from 'ckeditor5';

function getTargetParagraphIndex(editor: MultiRootEditor, isEditorFocused: boolean): number | null {
  const elements = Array.from(
    editor.model.document.getRoot('content')?.getChildren() ?? [],
  );
  const currentPosition = editor.model.document.selection.getLastPosition();

  return isEditorFocused
    ? currentPosition?.path[0] ?? null
    : Math.max(0, elements.length - 1);
}

function isElementEmpty(element: any, editor: MultiRootEditor): boolean {
  const childCount = element.childCount || 0;
  let isEmpty = childCount === 0;

  if (childCount === 1) {
    const elementHtml = editor.data.stringify(element);
    isEmpty = isEmptyRichText(elementHtml)
      || elementHtml === EMPTY_CKE_TEXT
      || elementHtml.trim() === '<p></p>'
      || elementHtml.trim() === '<p>&nbsp;</p>';
  }

  return isEmpty;
}

function positionCursorAfterInsertion(
  editor: MultiRootEditor,
  writer: ModelWriter,
  targetIndex: number,
  initialElementCount: number,
) {
  const paragraphs = Array.from(
    editor.model.document.getRoot('content')?.getChildren() ?? [],
  );

  const lastInsertedIndex = targetIndex + (paragraphs.length - initialElementCount);
  const lastInsertedElement = paragraphs[lastInsertedIndex];

  if (!isNil(lastInsertedElement)) {
    writer.setSelection(writer.createPositionAt(lastInsertedElement, 'end'));
  }
}

function insertTemplateAtPosition(editor: MultiRootEditor, data: string, targetIndex: number) {
  editor.model.change((writer: ModelWriter) => {
    const paragraphs = Array.from(
      editor.model.document.getRoot('content')?.getChildren() ?? [],
    );
    const targetElement = paragraphs[targetIndex];

    if (!isNil(targetElement)) {
      const isEmpty = isElementEmpty(targetElement, editor);

      const insertPosition = isEmpty
        ? writer.createPositionBefore(targetElement)
        : writer.createPositionAfter(targetElement);

      const modelFragment = editor.data.toModel(
        editor.data.processor.toView(normalizeContent(data)),
      );

      writer.insert(modelFragment, insertPosition);

      if (isEmpty) {
        writer.remove(targetElement);
      }

      positionCursorAfterInsertion(
        editor,
        writer,
        targetIndex,
        paragraphs.length,
      );
    }
  });

  setTimeout(() => {
    editor.editing.view.focus();
  }, 10);
}

export default function TemplateToolbarItem({
  editor,
  id,
  controls,
}: ToolbarItemProps) {
  const [dialogParams, setDialogParams] = useState<VisitNoteTemplateDialogParams | null>(null);
  const isEditorFocusedRef = useRef(false);
  return (
    <>
      <ToolTip title={isNil(id) ? 'Select section to use template' : 'Templates'}>
        <span>
          <IconButton
            onMouseDown={() => {
              if (!isNil(editor)) {
                isEditorFocusedRef.current = editor.ui.focusTracker.isFocused;
              }
            }}
            onClick={() => {
              if (!isNil(editor)) {
                const targetIndex = getTargetParagraphIndex(editor, isEditorFocusedRef.current);

                if (!isNil(targetIndex)) {
                  setDialogParams({
                    content: editor?.getData({ rootName: 'content' }),
                    section: id,
                    actions: controls as VisitNoteTemplateDialogAction[],
                    onSelect: (data = '') => {
                      if (!isNil(editor) && notEmptyString(data)) {
                        insertTemplateAtPosition(editor, data, targetIndex);
                      }
                    },
                  });
                }
              }
            }}
            disabled={isNil(id)}
            className="nw-template-button"
          >
            <TemplateIcon className="!w-4 !h-4 !text-inherit" />
          </IconButton>
        </span>
      </ToolTip>
      {!isNil(dialogParams) && (
        <TemplateDialog
          {...dialogParams}
          onClose={() => { setDialogParams(null); }}
        />
      )}
    </>
  );
}
